// Angular
import { Router } from '@angular/router';
import { Injectable, Injector } from '@angular/core';

// NGRX & RXJS
import { of } from 'rxjs';
import { Store } from '@ngrx/store';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import {
	map,
	switchMap,
	catchError,
	withLatestFrom,
	mergeMap,
	tap
} from 'rxjs/operators';

// OLCI
import * as _ from 'lodash';
import * as olci from '../actions';
import * as fromRoot from '../reducers';
import { constants } from '../constants';
import { OlciUtils } from './olcieffects.utils';
import * as guestActions from '../actions/guest.actions';
import { FeatureFlagDictionaryEnum } from '../shared/enums';
import * as guestDetailsActions from '../actions/guest-details.actions';
import { resourceBundleManagerMock } from '../shared/mocks/resource-bundle';
import { IGovernanceContentParams } from '../models/governance-content.payload';
import { IHealthCenterProtocolsParams } from '../models/healthyCenter-protocols.payload';
import { IRetrieveCardCountryResponse, IRetrieveCheckinStatusRequestPayload } from '../models';

// Services
import { LoaderService } from '../modules/loading/loader.service';
import { GuestService } from '../modules/core/services/guest.service';
import { BookingService } from '../modules/core/services/booking.service';
import { GatingServices } from '../modules/core/services/gating.services';
import { OexpensesService } from '../modules/core/services/oexpenses.service';
import { ContractsService } from '../modules/core/services/contracts.service';
import { FeatureFlagsService } from '../modules/core/services/feature-flags.service';
import { MicroServicesService } from '../modules/core/services/micro-services.service';
import { TravelDocumentsService } from '../modules/core/services/travel-documents.service';
import { TelemetryErrorData } from '../shared/interfaces';
@Injectable()
export class OlciEffects {
  private enableResourceBundleMock = false;
  private readonly RESOURCE_BUNDLE_MOCK: any = resourceBundleManagerMock();

  private travelDocumentsService: TravelDocumentsService;
  private microServicesService: MicroServicesService;
  private loader: LoaderService;
  private guestService: GuestService;
  private bookingService: BookingService;
  private gatingService: GatingServices;
  private oExpensesService: OexpensesService;
  private olciUtils: OlciUtils;
  private contractsService: ContractsService;
  private featureFlagService: FeatureFlagsService;

	constructor(
    private readonly injector : Injector,
		private readonly actions$: Actions,
		private readonly router: Router,
		private readonly store: Store<fromRoot.AppState>
	) {
    this.injectServices();
    this.onInitFeatureFlags();
  }

  injectServices() {
    this.travelDocumentsService = this.injector.get<TravelDocumentsService>(TravelDocumentsService);
    this.microServicesService = this.injector.get<MicroServicesService>(MicroServicesService);
    this.loader = this.injector.get<LoaderService>(LoaderService);
    this.guestService = this.injector.get<GuestService>(GuestService);
    this.bookingService = this.injector.get<BookingService>(BookingService);
    this.gatingService = this.injector.get<GatingServices>(GatingServices);
    this.oExpensesService = this.injector.get<OexpensesService>(OexpensesService);
    this.olciUtils = this.injector.get<OlciUtils>(OlciUtils);
    this.contractsService = this.injector.get<ContractsService>(ContractsService);
    this.featureFlagService = this.injector.get<FeatureFlagsService>(FeatureFlagsService);
  }

  onInitFeatureFlags() {
    this.enableResourceBundleMock = this.featureFlagService.isFeatureEnabled(
      FeatureFlagDictionaryEnum.ENABLE_RESOURCE_BUNDLE_MOCK
    );
  }

	loadResourceBundleContent$ = createEffect(() => {
		return this.actions$.pipe(
		ofType(olci.OlciActionTypes.LOAD_CONTENT_ACTION),
		switchMap(() => this.enableResourceBundleMock
      ? of(new olci.LoadContentSuccessAction(this.RESOURCE_BUNDLE_MOCK))
      : this.microServicesService.getResourceBundle().pipe(
				map(
          content => new olci.LoadContentSuccessAction(content)
        ),
				catchError(
          error => of(new olci.LoadContentSuccessAction(error))
        )
			)
		));
	});

	// Sailing Information
	sailingInformation$ = createEffect(() => {
		return this.actions$.pipe(
		ofType(olci.OlciActionTypes.POST_SAILING_INFORMATION),
		map(action => (action as any).payload),
		switchMap((payload: any) => {
			const voyages = this.getVoyagesData(payload);
			if(voyages !== null){
				return this.microServicesService.getSailingInformation(voyages).pipe(
					map(sailingInfo => {
						const mapped = this.bookingService.mapSailingInfoToStoreFormat(sailingInfo);
						return new olci.PostSailingInformationSuccessAction(mapped);
					}),
					catchError(error => {
						return of(new olci.PostSailingInformationSuccessAction(error));
					})
				);
			} else {
				const error = { status: 400, message: "No booking data found" }
				return of(new olci.PostSailingInformationFailureAction(error));
            }
		}));
	});

  private getVoyagesData(payload: any) {
    if (!payload?.sailingIds || payload.sailingIds.length === 0) {
      return null;
    }
    return payload?.sailingIds
      .map(
        ({ shipCode, sailDate }: { shipCode: string; sailDate: string }) =>
          shipCode + sailDate
      )
      .join(', ');
  }

	//Travel questionnaire
	travelQuestionnaire$ = createEffect(() => {
		return this.actions$.pipe(
		ofType(olci.OlciActionTypes.LOAD_TRAVEL_QUESTIONNAIRE),
		map(action => (action as any).payload),
		switchMap(payload => {
			return this.microServicesService.getTravelQuestionnaire(payload).pipe(
				map(travelQuestionnaire => {
					return new olci.LoadTravelQuestionnaireActionSuccess(travelQuestionnaire);
				}),
				catchError(error => {
					return of(new olci.LoadTravelQuestionnaireActionSuccess(error));
				})
			);
		}));
	});

	// /*************** Personal Info  *****************************/
  loadPdetailResources$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(olci.OlciActionTypes.PDETAIL_CONTENT_LOAD),
      switchMap(() => {
        return this.microServicesService.getPersonalDetailContent().pipe(
          map(content => new olci.LoadPdetailContentSuccessAction(content)),
          catchError(error => {
            return of(new olci.LoadPdetailContentSuccessAction(error));
          })
        );
      }),
      catchError(error => of(new olci.LoadPdetailContentSuccessAction(error)))
    );
  });

	loadBeginDetails$ = createEffect(() => {
		return this.actions$.pipe(
		ofType(olci.GuestDetailsActionTypes.LOAD_BEGIN_DETAILS),
		map(action => (action as any).payload),
		switchMap(payload => {
			return this.microServicesService.getBeginDetails(payload).pipe(
				withLatestFrom(this.store),
				mergeMap(([pInfo, store]) => {
					const successPayload = Object.assign({}, pInfo, {
						loggedInPaxId: store.session.reservationRetrievalResp.loggedInPaxId
					});
					const checkinToken = _.get(pInfo, 'payload.checkinToken', '');
					return [
						new olci.LoadBeginDetailsSuccessAction(successPayload),
						new guestActions.UpdateGuestsInfoFromBeginMicroservice(pInfo),
						new olci.UpdateCheckinToken(checkinToken)
					];
				}),
				catchError(error => {
					return [new olci.LoadBeginDetailsSuccessAction(error)];
				})
			);
		}));
	});

	loadGuestPercentage$ = createEffect(() => {
		return this.actions$.pipe(
		ofType(guestActions.GuestActionTypes.GET_PERCENTAGE_COMPLETION),
		map(action => (action as any).payload),
		switchMap(params => {
			if (params.guestReservationIds && params.guestReservationIds.length > 0) {
				return this.microServicesService.getCheckinStatus(params).pipe(
					map(guestsProgress => {
						const mapped = this.guestService.modulesValuesFromPercentages(guestsProgress);
						return new guestActions.UpdateCompletionPercentageGuestAction(mapped);
					}),
					catchError(error => {
						return of(new guestActions.UpdateCompletionPercentageGuestAction(error));
					})
				);
			} else {
				return of(
					new guestActions.UpdateCompletionPercentageGuestAction({
						status: constants.HTTP_STATUS_CODE.BACKEND_ERROR
					})
				);
			}
		}));
	});

  loadValidDocumentTypes$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(olci.GuestDetailsActionTypes.LOAD_GUEST_DOCUMENT_TYPES),
      map((action: any) => {
        return 'payload' in action ? action.payload : action;
      }),
      switchMap((params) => {
        return this.retrieveValidDocumentTypes(params);
      })
    );
  });

  retrieveValidDocumentTypes(params: any) {
    const guestId = parseInt(params.guests[0].guestId as string);
    return this.microServicesService.retrieveValidTravelDocumentList(params).pipe(
      map(content => {
        const { emptyTravelDocuments } = this.travelDocumentsService.retrieveEmptyTravelDocuments(content);
        const newContent = this.travelDocumentsService.retrieveValidTravelDocuments(
          emptyTravelDocuments,
          guestId
        );

        return new olci.LoadGuestDocumentTypesSuccessAction(
          { documentTypeList: newContent, guestId, type: params.type, status: content.status }
        );
      }),
      catchError(error => of(new olci.LoadGuestDocumentTypesSuccessAction(error)))
    );
  }

    saveGuestPhoto$ = createEffect(() => {
		return this.actions$.pipe(
		ofType(guestActions.GuestActionTypes.SAVE_GUEST_PHOTO),
		map(action => (action as any).payload),
		switchMap(payload => {
			this.loader.showLoader();
			return this.microServicesService.saveGuestSecurityPhoto(payload).pipe(
				map((res: any) => {
					const successActionData = {
						imageData: payload.imageData,
						guestID: payload.guestID ?? payload.reservationId.guestId,
						guestPhotoId: res?.guestPhotoID ?? res?.payload?.guestPhotoId,
						status: res?.status
					}
					return new guestActions.SaveGuestPhotoSuccessAction(successActionData);
				}),
				catchError(error => {
					return of(new guestActions.SaveGuestPhotoSuccessAction(error));
				})
			);
		}));
	});

	UpdateGuestPhoto$ = createEffect(() => {
		return this.actions$.pipe(
		ofType(guestActions.GuestActionTypes.UPDATE_GUEST_PHOTO),
		map(action => (action as any).payload),
		switchMap(payload => {
			this.loader.showLoader();
			return this.microServicesService.updateGuestSecurityPhoto(payload).pipe(
				map((res: any) => {
					const successActionData = {
                        imageData: payload.imageData,
                        guestID: payload.guestID ?? payload.reservationId.guestId,
                        guestPhotoId: res?.guestPhotoID ?? res?.payload?.guestPhotoId,
                        status: res?.status
                    };

                    return new guestActions.UpdateGuestPhotoSuccessAction(successActionData);
                }),
				catchError(error => {
					return of(new guestActions.UpdateGuestPhotoSuccessAction(error));
				})
			);
		}));
	});

  retrieveGuestsSecurityPhotoList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(guestActions.GuestActionTypes.RETRIEVE_GUEST_SECURITY_PHOTO_LIST),
      map((action: any) => action.payload),
      switchMap((payload) => {
        return this.microServicesService
          .retrieveGuestsSecurityPhotoList(
            this.olciUtils.retrieveRequestPayloadPhotos(payload)
          )
          .pipe(
            map((res) => {
              return new guestActions.RetrieveGuestSecurityPhotoListSuccessAction(
                res
              );
            }),
            catchError((error) => {
              return of(new guestActions.UpdateGuestPhotoSuccessAction(error));
            })
          );
      })
    );
  });

	navigateToError$ = createEffect(() => this.actions$.pipe(
		ofType(olci.OlciActionTypes.NAVIGATE_TO_ERROR),
		tap(() => {
			this.router.navigate([constants.ROUTES.ERROR]);
			this.store.dispatch(new olci.SetResponseErrorClearAction());
		})),
		{ dispatch: false }
	);

  survey$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(guestActions.GuestActionTypes.POST_SURVEY),
      map((action) => (action as any).payload),
      switchMap((payload) =>
        this.microServicesService
          .savePassengerSurvey(this.olciUtils.retrieveRequestPayloadSurvey(payload))
          .pipe(
            map(() => {
              return new guestDetailsActions.SaveGuestSurveySuccessAction(
                payload
              );
            }),
            catchError((error) => of(new olci.LoadFailAction(error)))
          )
      )
    );
  });

  // /************* Cruise Contract ***************/

	loadCruiseContractDetails$ = createEffect(() => {
		return this.actions$.pipe(
		ofType(olci.OlciActionTypes.LOAD_CRUISE_CONTRACT),
		map(action => (action as any).payload),
		switchMap(payload => {
			return this.microServicesService.getAuthorizedConsentsAll(payload).pipe(
				map((res) => {
					const authorizedConsentsStore = this.contractsService.mapToStoreFormat(res);
					return new olci.LoadCruiseContractSuccessAction(authorizedConsentsStore);
				}),
				catchError(error => of(new olci.LoadCruiseContractSuccessAction(error)))
			);
		}));
	});

	clearSessionStorage$ = createEffect(() => this.actions$.pipe(
		ofType(olci.OlciActionTypes.CLEAR_SESSION_STORAGE),
		map(action => this.gatingService.clearSessionStorage())
		),
		{ dispatch: false }
	);

	// /************* OnBoard Expense ***************/

	loadOnboardExpenses$ = createEffect(() => {
		return this.actions$.pipe(
		ofType(olci.OlciActionTypes.LOAD_GUESTS_ONBOARD_EXPENSES),
		switchMap(() => {
			return this.microServicesService.getRetrieveOnboardPayment(this.oExpensesService.getRetrieveOnboardPaymentPayload()).pipe(
				withLatestFrom(this.store),
				map(([res, store]) => {
					if (res.status === constants.HTTP_STATUS_CODE.SUCCESS) {
						const accountCards = res.payload && res.payload.accountCards ? res.payload.accountCards : [];
						const guestsPaymentAccounts = this.olciUtils.mapExpensesInfoToNewStoreFormat(res);
						const bookingId = store.session.reservationRetrievalResp.bookingId;
						return new olci.LoadGuestsOnboardExpensesSuccess({
							bookingId,
							accountCards,
							guestsPaymentAccounts,
							status: constants.HTTP_STATUS_CODE.SUCCESS
						});
					} else {
						throw res;
					}
				}),
				catchError(error => {
					return of(new olci.LoadGuestsOnboardExpensesSuccess(error));
				})
			);
		}));
	});

  retrieveCardCountry$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(olci.OlciActionTypes.LOAD_CREDIT_CARD_COUNTRY),
      map(action => (action as any).payload),
      switchMap(payload => {
        return this.microServicesService.getRetrieveCreditCardCountry(payload)
          .pipe(
            mergeMap(
              (res: IRetrieveCardCountryResponse) => [new olci.LoadCreditCardCountrySuccess(res)]
            ),
            catchError(
              error => of(new olci.LoadCreditCardCountryFailure(error))
            )
          )
      })
    )
  });

	saveExpenseAccount$ = createEffect(() => {
		return this.actions$.pipe(
		ofType(olci.OlciActionTypes.POST_ONBOARD_EXPENSE),
		map(action => (action as any).payload),
		switchMap(payload => {
      const { url, telemetryErrorData } = this.getUrlAndTelemetryErrorOnboardPayment();
			return this.microServicesService.createUpdateOnboardPayment(payload, url, telemetryErrorData).pipe(
				mergeMap(res => {
					const { paymentAccounts: [ { account } ]} = payload;
					if (
						Object.keys(res.payload).length === 0 ||
						res.status !== constants.HTTP_STATUS_CODE.SUCCESS ||
						res.payload.paymentAccounts.length === 0
					) {
						this.loader.hideLoader();
            const warningMessage = res.payload?.warnings[0]?.warningMessage ?? '';
            this.oExpensesService.sendEventToAnalyticsAndTelemetry(
              warningMessage,
              url,
              telemetryErrorData,
              'GC-026'
            );
						return [
							new olci.PostOnboardExpenseSelectedCCAction({...account, isValid: false}),
							new olci.LoadFailAction(res)
						];
					}
					const checkinStatusPayload: IRetrieveCheckinStatusRequestPayload = this.guestService.getAllGuestsReservationIds();
					return [new olci.PostOnboardExpenseSuccessAction(res), new olci.PostOnboardExpenseSelectedCCAction({...account, isValid: true}), new guestActions.GetPercentageCompletionAction(checkinStatusPayload)];
				}),
				catchError(error => {
					this.loader.hideLoader();
					return of(new olci.LoadFailAction(error));
				})
			);
		}));
	});

  private getUrlAndTelemetryErrorOnboardPayment(): {
    url: string;
    telemetryErrorData: TelemetryErrorData;
  } {
    return {
      url: `${this.microServicesService.apiUrl}/${this.microServicesService.locale}/${this.microServicesService.brand}/${this.microServicesService.channel}${this.microServicesService.endpoints.API_MICROSERVICE_CREATE_UPDATE_ONBOARD_PAYMENT}/${this.microServicesService.vdsId}/${this.microServicesService.endpoints.API_MICROSERVICE_SAVE_ONBOARD_PAYMENT}`,
      telemetryErrorData: {
        message: `Unexpected error creating/updating onboard expenses`,
        lookup: 'GC-026-POST-CREATE-UPDATE-ONBOARD-PAYMENT-FAILED',
      } as TelemetryErrorData,
    };
  }

	// /************* Set Sail Pass***************/

  loadSSPass$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(olci.OlciActionTypes.GET_SET_SAIL_PASS),
      switchMap((action) =>
        this.microServicesService.getSetSailPass((action as any).payload).pipe(
          tap((res) => {
            res.type = olci.OlciActionTypes.GET_SET_SAIL_PASS_SUCCESS;
            this.store.dispatch(new olci.GetSetSailpassSuccessAction(res));
          }),
          catchError((error) => {
            this.store.dispatch(new olci.NavigateToError());
            return of(new olci.GetSetSailpassSuccessAction(error));
          })
        )
      )
    );
  });

	getCoreGovernanceContent$ = createEffect(() => {
		return this.actions$.pipe(
		ofType(olci.OlciActionTypes.FETCH_GOVERNANCE_CONTENT),
		map(action => (action as any).payload),
		switchMap((params: IGovernanceContentParams) =>
			this.microServicesService.getGovernanceContent(params).pipe(
				map((govContent) => {
					return new olci.FetchGovernanceContentSuccess(govContent.payload);
				}),
				catchError(() => {
					return of(new olci.FetchGovernanceContentFailure(null));
				})
			)
		));
	});

	getHealthCenterProtocols$ = createEffect(() => {
		return this.actions$.pipe(
		ofType(olci.OlciActionTypes.FETCH_HEALTH_CENTER_PROTOCOLS),
		map(action => (action as any).payload),
		switchMap((params: IHealthCenterProtocolsParams) =>
			this.microServicesService.getHealthCenterProtocols(params).pipe(
				map((healthyCenterProtocols) => {
					return new olci.FetchHealthCenterProtocolsSuccess(healthyCenterProtocols.payload);
				}),
				catchError(error => {
					return of(new olci.FetchHealthCenterProtocolsFailure(error));
				})
			)
		));
	});

	getCountries$ = createEffect(() => {
		return this.actions$.pipe(
      ofType(olci.OlciActionTypes.GET_COUNTRIES),
      switchMap(() =>
        this.microServicesService.getCountries().pipe(
          map((countries) => {
            return new olci.GetCountriesSuccess(countries.payload);
          }),
          catchError(error => {
            return of(new olci.GetCountriesFailure(error));
          })
        )
      )
    );
  });
}